import { api } from './client'
import type {
  ComprobanteFiscal,
  EstadoVenta,
  MetodoPago,
  Page,
  ResumenVentasFiltrado,
  Venta,
  VentaInput,
  VentaLoteInput,
  VentaLoteResponse,
} from '@/types/api'

export interface VentaFiltros {
  fecha_inicio?: string
  fecha_fin?: string
  turno_id?: number | null
  categoria_id?: number | null
  producto_id?: number | null
  metodo_pago?: MetodoPago
  usuario_id?: number | null
  estado?: EstadoVenta
  page?: number
  size?: number
}

export type FormatoExport = 'xlsx' | 'csv'

export interface ExportResultado {
  blob: Blob
  filename: string
  filas: number
  truncado: boolean
}

/** Quita filtros vacíos: un `""` o `null` en la query rompería el parseo del backend. */
function limpiar(filtros: VentaFiltros): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(filtros)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v as string | number
  }
  return out
}

function nombreDesdeHeader(header: string | undefined, fallback: string): string {
  const m = header?.match(/filename="?([^";]+)"?/)
  return m?.[1] ?? fallback
}

export const ventasApi = {
  registrar: (payload: VentaInput) =>
    api.post<Venta>('/ventas', payload).then((r) => r.data),
  registrarLote: (payload: VentaLoteInput) =>
    api.post<VentaLoteResponse>('/ventas/lote', payload).then((r) => r.data),
  recientes: (limit = 15) =>
    api.get<Venta[]>('/ventas/recientes', { params: { limit } }).then((r) => r.data),
  buscar: (filtros: VentaFiltros = {}) =>
    api.get<Page<Venta>>('/ventas', { params: limpiar(filtros) }).then((r) => r.data),
  resumen: (filtros: VentaFiltros = {}) =>
    api
      .get<ResumenVentasFiltrado>('/ventas/resumen', { params: limpiar(filtros) })
      .then((r) => r.data),
  anular: (id: number, motivo: string) =>
    api.post<Venta>(`/ventas/${id}/anular`, { motivo }).then((r) => r.data),
  /** Documento equivalente POS de la venta (404 si no tiene). */
  documentoFiscal: (id: number) =>
    api.get<ComprobanteFiscal>(`/ventas/${id}/documento-fiscal`).then((r) => r.data),
  enviarComprobante: (id: number, email: string) =>
    api
      .post<{ message: string }>(`/ventas/${id}/comprobante`, { email }, { timeout: 30_000 })
      .then((r) => r.data),
  exportar: async (filtros: VentaFiltros, formato: FormatoExport): Promise<ExportResultado> => {
    // Va por axios y no por un <a href> para que el refresh del token siga aplicando.
    const { page: _p, size: _s, ...resto } = filtros
    const r = await api.get<Blob>('/ventas/export', {
      params: { ...limpiar(resto), formato },
      responseType: 'blob',
      timeout: 120_000,
    })
    return {
      blob: r.data,
      filename: nombreDesdeHeader(r.headers['content-disposition'], `ventas.${formato}`),
      filas: Number(r.headers['x-export-filas'] ?? 0),
      truncado: r.headers['x-export-truncado'] === '1',
    }
  },
}
